import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ObjectMap } from 'mf_core/query/api-hooks';
import { useLocalization } from '@im/react-localization';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import InsightsFooter from './internal/InsightsFooter';
import InsightsContent from './internal/InsightsContent';
import { InsightsMessagingQueueSkeleton } from './internal/InsightsMessagingQueueSkeleton';

type Props = {
  dismissAllHandler?: () => void;
  maxDropHeight?: number;
  onClickInsightsCenter?: () => void;
};

export default function InsightsMessagingQueue(props: Props) {
  const location = useLocation();
  const { translate } = useLocalization();
  const { dismissAllHandler, maxDropHeight, onClickInsightsCenter } = props;
  const [loadInsightsData, setLoadInsightsData] = useState(false);
  const [currentPage, setCurrentPage] = useState('Home Page');
  const [chipsDetails, setChipsDetails] = useState<ObjectMap[]>([]);
  const [insightsDetails, setInsightsDetails] = useState<ObjectMap | null>(
    null
  );

  const pageNames: ObjectMap = {
    '/insight/insights-v2': 'Insights Center',
    '/my/cart': 'cart checkout',
    '/my/dashboard': 'Home Page',
    '/product/productdetails': 'Details Page',
    '/product/productsearch': 'Search Page',
  };
  const enableInsightsPaths = [
    '/insight/insights-v2',
    '/my/cart',
    '/my/dashboard',
    '/product/productsearch',
    '/product/productdetails',
    '/product/subscriptioncategories',
  ];

  useEffect(() => {
    const pathName = location?.pathname ?? '';
    let timer: any = null;
    setLoadInsightsData(false);

    if (enableInsightsPaths.includes(pathName)) {
      if (
        (pathName === '/product/productsearch' &&
          (location?.search?.includes('categories') ||
            location?.search?.includes('category'))) ||
        pathName === '/product/subscriptioncategories'
      ) {
        setCurrentPage('Product Category');
      } else if (
        pathName === '/product/productsearch' &&
        location?.search?.includes('vendor')
      ) {
        setCurrentPage('Vendor List');
      } else {
        setCurrentPage(pageNames[pathName]);
      }

      timer = setTimeout(() => {
        setLoadInsightsData(true);
      }, 100);
    }

    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  return (
    <StyledBoxWrapper component="div">
      <Stack direction="column" height="100%" justifyContent="space-between">
        <Stack direction="column">
          <Stack
            alignItems="center"
            direction="row"
            justifyContent="flex-start"
          >
            <Typography sx={{ fontWeight: 'bold' }} variant="body1">
              {translate('Insights.Card_TitleText')}
            </Typography>
            <CustomWidthTooltip title={'Tooltip Text'}>
              <InfoOutlinedIcon
                color="disabled"
                fontSize="small"
                sx={{ display: 'none', ml: 1 }}
              />
            </CustomWidthTooltip>
          </Stack>
          <StyledPageNameTypography variant="body2">
            {translate(`Insights.${currentPage.replace(/\s/g, '')}`)}
          </StyledPageNameTypography>
          {!loadInsightsData && <InsightsMessagingQueueSkeleton />}
          {loadInsightsData && (
            <InsightsContent
              chipsDetails={chipsDetails}
              currentPage={currentPage}
              insightsDetails={insightsDetails}
              maxDropHeight={maxDropHeight}
              setChipsDetails={setChipsDetails}
              setInsightsDetails={setInsightsDetails}
            />
          )}
        </Stack>
        <InsightsFooter
          dismissAllHandler={dismissAllHandler}
          onClickInsightsCenter={onClickInsightsCenter}
          showInsightsCenter={location?.pathname !== '/insight/insights-v2'}
        />
      </Stack>
    </StyledBoxWrapper>
  );
}

const StyledBoxWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: '#FFF',
  borderRadius: '4px',
  display: 'inline-block',
  height: '100%',
  minWidth: 350,
  padding: theme.spacing(2),
}));

const StyledPageNameTypography = styled(Typography)(({ theme }) => ({
  color: '#a9a8a8',
  fontSize: '11px',
  fontWeight: 'bold',
  lineHeight: '10px',
}));

const CustomWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    background: '#333333 !important',
    borderRadius: '4px',
    boxShadow: 'none',
    color: '#fff !important',
    fontSize: '12px !important',
  },
});
